import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { all_routes } from "../../../../router/all_routes";
import ImageWithBasePath from "../../../../core/common/imageWithBasePath";
import { Form, Alert, Typography, Steps, Button} from "antd";
import { 
  UserOutlined, 
  ApartmentOutlined, 
  CreditCardOutlined, 
  GiftOutlined, 
  LoginOutlined, 
  LoadingOutlined,
  CheckCircleFilled
} from "@ant-design/icons";
import { CompanyStatus } from "../../../../types/company.types";
import { useTranslation } from "react-i18next";
import { showNotification, cleanFormData } from "./createCompanyPublic/registrationHelpers";
import companyService from '../../../../services/company/company.service';
import authService from '../../../../services/auth/auth.service';
import { useSubscription } from '../../../../hooks/useSubscription';
import { productService, Product } from "../../../../services/product/product.service";
import subService from '../../../../services/sub/sub.service';
import CompanyForm from "./createCompanyPublic/CompanyForm";
import AdminForm from "./createCompanyPublic/AdminForm";
import PricingForm from "./createCompanyPublic/PricingForm";
import './createCompanyPublic/register.css'
import { useDispatch, useSelector } from "react-redux";
import { checkAuth } from "../../../../store/slices/auth.slice";
import { useCompanyData } from '../../../../hooks/useCompanyData';
// const CompanyForm = lazy(() => import("./createCompanyPublic/CompanyForm"));
// const AdminForm = lazy(() => import("./createCompanyPublic/AdminForm"));
// const PricingForm = lazy(() => import("./createCompanyPublic/PricingForm"));

const { Title, Text, Paragraph } = Typography;
const { Step } = Steps;

type PasswordField = "password";

const Login3 = () => {
  const [companyForm] = Form.useForm();
  const [adminForm] = Form.useForm();
  const [pricingForm] = Form.useForm();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [stepLoading, setStepLoading] = useState<number | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [pricingError, setPricingError] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [createdCompanyId, setCreatedCompanyId] = useState<number | null>(null);
  const [createdCompanyData, setCreatedCompanyData] = useState<any>(null);
  const [onboardingSecret, setOnboardingSecret] = useState<string | null>(null);
  const [adminData, setAdminData] = useState<any>(null);
  const [userCount, setUserCount] = useState(5);
  const [selectedPlan, setSelectedPlan] = useState<'trial' | 'paid'>('trial');
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [appliedCoupon, setAppliedCoupon] = useState<any>(null);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [products, setProducts] = useState<Product[]>([]);
  const [productsLoading, setProductsLoading] = useState(true);
  
  // Billing address
  const [billingAddress, setBillingAddress] = useState<any>(null);
  const { companyData, loading: companyLoading } = useCompanyData();

  const dispatch = useDispatch();
  const { t } = useTranslation();

  // Subscription hook
  const {
    createCheckoutSession,
    loading: subscriptionLoading
  } = useSubscription(false);

  // Profesyonel adım yapısı
  const steps = [
    {
      title: 'Şirket Bilgileri',
      description: 'Temel şirket bilgilerinizi girin',
      icon: <ApartmentOutlined />,
      status: completedSteps.includes(0) ? 'finish' : currentStep === 0 ? 'process' : 'wait'
    },
    {
      title: 'Yönetici Hesabı',
      description: 'Sistem yöneticisi hesabını oluşturun',
      icon: <UserOutlined />,
      status: completedSteps.includes(1) ? 'finish' : currentStep === 1 ? 'process' : 'wait'
    },
    {
      title: 'Plan Seçimi',
      description: 'Fiyatlandırma planınızı belirleyin',
      icon: <CreditCardOutlined />,
      status: completedSteps.includes(2) ? 'finish' : currentStep === 2 ? 'process' : 'wait'
    },
  ];

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setProductsLoading(true);
        setPricingError(null);
        const fetchedProducts = await productService.getAllProducts();
        if (fetchedProducts.length === 0) {
          throw new Error("Fiyatlandırma bilgileri alınamadı.");
        }
        setProducts(fetchedProducts);
      } catch (error) {
        console.error("Fiyatlandırma ürünleri alınırken hata oluştu:", error);
        setPricingError("Fiyatlandırma bilgileri yüklenirken bir hata oluştu. Lütfen sayfayı yenileyin.");
        showNotification('error', '❌ Fiyatlandırma Hatası', 'Fiyatlandırma bilgileri alınamadı.');
      } finally {
        setProductsLoading(false);
      }
    };

    fetchProducts();
  }, []);

  // Sayfa yükleme durumu
  useEffect(() => {
    if (!productsLoading) {
      // Ürünler yüklendikten sonra küçük bir gecikme ile yükleme ekranını kapat
      const timer = setTimeout(() => {
        setIsPageLoading(false);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [productsLoading]);

  // Genel ilerleme hesaplama
  const overallProgress = useMemo(() => {
    const totalSteps = 3;
    const completedCount = completedSteps.length;
    const currentStepProgress = currentStep > completedCount ? 33.33 : 0;
    return Math.min(100, (completedCount * 33.33) + currentStepProgress);
  }, [completedSteps, currentStep]);

  // Yeni paket yapısına uygun fiyat hesaplama
  const calculatePricing = useMemo(() => {
    if (products.length === 0 || !selectedProduct) {
      return {
        monthly: 0, packageTotal: 0, packageTotalWithTax: 0,
        savings: 0, couponDiscount: 0, 
        currency: 'TRY', breakdown: {}, monthlyUserPrice: 0,
        annualDiscountPercent: 0, basePackagePrice: 0,
      };
    }
    
    const monthlyUserPrice = selectedProduct.pricePerUserPerMonth;
    
    // 1. Temel paket fiyatı hesaplama (Kuponsuz)
    const basePackagePrice = userCount * selectedProduct.durationMonths * monthlyUserPrice;
    
    // 2. Kupon indirimi uygula
    let finalPackagePrice = basePackagePrice;
    let couponDiscount = 0;
    
    if (appliedCoupon && appliedCoupon.isValid) {
      if (appliedCoupon.originalAmount === basePackagePrice) {
        finalPackagePrice = appliedCoupon.finalAmount;
        couponDiscount = appliedCoupon.savings;
      }
    }
  
    // 3. Final fiyat üzerinden vergi hesaplama
    const taxRate = 0.20;
    const packageTotalWithTax = finalPackagePrice * (1 + taxRate);
    
    // 4. Aylık eşdeğer hesaplama
    const monthlyEquivalent = finalPackagePrice / selectedProduct.durationMonths;
    
    return {
      basePackagePrice,
      monthly: monthlyEquivalent,
      packageTotal: finalPackagePrice,
      packageTotalWithTax: packageTotalWithTax,
      savings: 0, // Paketler arası tasarruf hesaplanabilir
      couponDiscount,
      currency: 'TRY',
      breakdown: {
        basePrice: basePackagePrice,
        userCount,
        durationMonths: selectedProduct.durationMonths,
        hasCoupon: !!appliedCoupon,
        couponCode: appliedCoupon?.code || null
      },
      monthlyUserPrice,
      annualDiscountPercent: 0,
    };
  }, [userCount, appliedCoupon, products, selectedProduct, pricingError]);
  
  // Retry mekanizması
  const handleSubmitWithRetry = async (submitFn: Function, maxRetries = 3) => {
    for (let i = 0; i < maxRetries; i++) {
      try {
        await submitFn();
        return;
      } catch (error) {
        if (i === maxRetries - 1) throw error;
        await new Promise(resolve => setTimeout(resolve, 1000 * (i + 1)));
      }
    }
  };

  // Adım 1: Şirket oluşturma
  const handleCompanySubmit = async (values: any) => {
    try {
      setStepLoading(0);
      setErrorMessage(null);

      const cleanedValues = cleanFormData(values, 'company');
      const companyData = {
        ...cleanedValues,
        status: CompanyStatus.ACTIVE,
        defaultBreakDuration: 60,
        settings: {
          workOnHolidays: false,
          workOnWeekends: false,
          includeManagerInReports: true,
          lateTolerance: false,
          lateTolerationMinutes: 5,
          earlyLeave: false,
          includeWeekendsInAnnualLeave: false,
          sendLeaveRequestEmails: true,
          requirePreApprovalForLeave: true,
          annualLeaveLimit: 23,
          shiftMode: "assignment" as const,
          fixedShiftStartTime: "09:00",
          fixedShiftEndTime: "18:00",
          fixedShiftDays: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
          ipControl: false,
          useFixedShift: false,
          maxDailyWorkHours: 8
        }
      };

      const createdCompany = await companyService.createCompany(companyData, values.otp, values.sessionToken);
      
      setCreatedCompanyId(createdCompany.id);
      setCreatedCompanyData(createdCompany);
      setOnboardingSecret(createdCompany.onboardingSecret || null);
      setCompletedSteps(prev => [...prev, 0]);
      setCurrentStep(1);
      
      showNotification('success', '🎉 Şirket Oluşturuldu!', 'Şirket bilgileriniz başarıyla kaydedildi. Şimdi yönetici hesabını oluşturun.');
    } catch (error: any) {
      setErrorMessage('Şirket oluşturulurken bir hata oluştu. Lütfen tekrar deneyin.');
      showNotification('error', '❌ Hata Oluştu', 'Şirket oluşturulurken bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setStepLoading(null);
    }
  };

  // Adım 2: Admin bilgilerini kaydetme
  const handleAdminSubmit = async (values: any) => {
    try {
      setStepLoading(1);
      setErrorMessage(null);

      const cleanedValues = cleanFormData(values, 'user');
      
      setAdminData({
        ...cleanedValues,
        companyId: createdCompanyId,
        role: 'admin',
        isActive: true
      });
      
      setCompletedSteps(prev => [...prev, 1]);
      setCurrentStep(2);
      
      showNotification('success', '✅ Yönetici Hesabı Hazırlandı!', 'Yönetici hesabı bilgileriniz kaydedildi. Son adım olarak planınızı seçin.');
    } catch (error: any) {
      setErrorMessage('Bilgiler kaydedilirken bir hata oluştu.');
      showNotification('error', '❌ Hata Oluştu', 'Bilgiler kaydedilirken bir hata oluştu.');
    } finally {
      setStepLoading(null);
    }
  };

  // Adım 3: Fiyatlandırma ve ödeme
  const handlePricingSubmit = async (values: any) => {
    try {
      setStepLoading(2);
      setErrorMessage(null);

      if (!onboardingSecret) {
        throw new Error('Lütfen şirket oluşturmayı tekrar deneyin.');
      }

      if (selectedPlan === 'paid') {
        // Admin bilgilerini geçici olarak localStorage'a kaydet (ödeme callback için)
        localStorage.setItem('tempNewAdminRegistration', JSON.stringify({
          companyId: createdCompanyId,
          adminData: adminData,
          onboardingSecret: onboardingSecret,
          isNewRegistration: true
        }));

        // IYZICO dokümantasyonuna göre yeni paket satın alma API'sini kullan
        if (!selectedProduct) {
          throw new Error('Lütfen bir paket seçin.');
        }

        // Backend interface'ine uygun request body formatı
        const purchaseData = {
          companyId: createdCompanyId!,
          userLimit: userCount,
          duration: selectedProduct.durationMonths,
          finalAmount: calculatePricing.packageTotalWithTax,
          billingAddress: billingAddress || {
            alias: "Varsayılan Adres",
            address: companyData?.address || "Test Adres",
            city: companyData?.city || "İstanbul", 
            zipCode: companyData?.zipCode || "34000",
            country: companyData?.country || "Turkey",
            contactName: `${adminData.firstName} ${adminData.lastName}`
          },
          couponCode: appliedCoupon?.code || undefined,
          isInternalRequest: true
        };

        const result = await subService.purchasePackage(purchaseData);
        // console.log('🔍 Checkout session result:', result);
        
        localStorage.setItem('checkoutsession', result.checkoutForm.token);
        localStorage.setItem('conversationId', result.checkoutForm.conversationId);
        
        if (result.checkoutForm) {
          const newWindow = window.open('', '_blank');
          if (newWindow) {
            newWindow.document.write(result.checkoutForm.checkoutFormContent);
            newWindow.document.close();
            showNotification('success', '💳 Ödeme Sayfası Açıldı!', 'Ödeme işleminizi yeni sekmede tamamlayın. Başarılı ödeme sonrası otomatik olarak sisteme giriş yapacaksınız.');
          } else {
            showNotification('error', 'Popup Engellendi', 'Ödeme sayfasını açmak için lütfen tarayıcınızda popup\'lara izin verin ve tekrar deneyin.');
          }
        }
      } else {//"token": "1174983d-fb8e-4bc5-90b1-32d5208a6a30",
        // Trial plan için login yap ve dashboard'a git
        const loginResponse = await authService.login({
          email: adminData.email,
          password: adminData.password,
          isMobile: false
        });

        authService.setToken(loginResponse.tokens.accessToken);
        authService.setUser(JSON.stringify(loginResponse.user));
        dispatch(checkAuth());

        showNotification('success', '🚀 Deneme Süreci Başladı!', '14 günlük ücretsiz deneme süreniz başladı. Tüm özellikleri keşfedin!');
        
        setCompletedSteps(prev => [...prev, 2]);
        
        // Dashboard'a yönlendir
        setTimeout(() => {
          navigate(all_routes.adminDashboard);
        }, 2000);
      }
      
    } catch (error: any) {
      console.error('Pricing submit error:', error);
      setErrorMessage('İşlem tamamlanırken bir hata oluştu.');
      showNotification('error', '❌ İşlem Başarısız', 'Bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setStepLoading(null);
    }
  };

  const handlePreviousStep = () => {
    setCurrentStep(prev => Math.max(0, prev - 1));
    // Tamamlanan adımlardan sonuncusunu kaldır
    setCompletedSteps(prev => prev.slice(0, prev.length - 1));
  };

  const getCurrentStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <CompanyForm
            form={companyForm}
            onFinish={handleCompanySubmit}
            loading={stepLoading === 0}
          />
        );
      case 1:
        return (
          <AdminForm
            form={adminForm}
            onFinish={handleAdminSubmit}
            loading={stepLoading === 1}
            onPrevious={() => {
              setCurrentStep(0);
              setCompletedSteps(prev => prev.filter(step => step !== 1));
            }}
          />
        );
      case 2:
        return (
          <PricingForm
            form={pricingForm}
            onFinish={handlePricingSubmit}
            loading={subscriptionLoading || stepLoading === 2}
            onPrevious={handlePreviousStep}
            userCount={userCount}
            setUserCount={setUserCount}
            selectedPlan={selectedPlan}
            setSelectedPlan={setSelectedPlan}
            selectedProduct={selectedProduct}
            setSelectedProduct={setSelectedProduct}
            products={products}
            calculatePricing={calculatePricing}
            appliedCoupon={appliedCoupon}
            setAppliedCoupon={setAppliedCoupon}
            user={adminData}
            billingAddress={billingAddress}
            setBillingAddress={setBillingAddress}
            companyData={createdCompanyData}
          />
        );
      default:
        return (
          <CompanyForm
            form={companyForm}
            onFinish={handleCompanySubmit}
            loading={stepLoading === 0}
          />
        );
    }
  };

  // Sayfa yükleme göstergesi
  if (isPageLoading) {
    return (
      <div className="page-loading-container">
        <div className="loading-content">
          <div className="loading-logo">
            <ImageWithBasePath
              src="assets/img/dinox-dark.png"
              alt="Dinox Logo"
              className="loading-brand-logo"
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modern-onboarding">
      {/* Background Animation */}
      <div className="background-animation">
        <div className="floating-shapes">
          <div className="shape shape-1"></div>
          <div className="shape shape-2"></div>
          <div className="shape shape-3"></div>
          <div className="shape shape-4"></div>
          <div className="shape shape-5"></div>
        </div>
      </div>

      <div className="container-fluid">
        <div className="row justify-content-center min-vh-100">
          <div className="col-12 col-xl-10 col-xxl-10">
            
            {/* Top Navigation Bar */}
            <div className="top-nav-bar">
              <div className="nav-content">
                <div className="logo-section">
                  <ImageWithBasePath
                    src="assets/img/dinox-dark.png"
                    alt="Dinox Logo"
                    className="brand-logo"
                  />
                </div>
                
                <div className="auth-actions">
                  <Text className="have-account-text">Zaten hesabınız var mı?</Text>
                  <Button 
                    type="default" 
                    icon={<LoginOutlined />}
                    onClick={() => navigate(all_routes.login)}
                    className="login-button"
                  >
                    Giriş Yap
                  </Button>
                </div>
              </div>
            </div>

            {/* Error Alert */}
            {pricingError && (
              <Alert
                message="Fiyatlandırma Hatası"
                description={pricingError}
                type="error"
                showIcon
                className="error-alert"
                style={{ marginBottom: '20px' }}
              />
            )}
            {errorMessage && (
              <Alert
                message="Bir hata oluştu"
                description={errorMessage}
                type="error"
                showIcon
                closable
                onClose={() => setErrorMessage(null)}
                className="error-alert"
              />
            )}

            {/* Hero Section */}
            <div className="hero-section">
              <div className="hero-content">
                <div className="hero-badge">
                  <GiftOutlined className="badge-icon" />
                  <span>14 Gün Ücretsiz Deneme</span>
                </div>
                
                <Title level={1} className="hero-title">
                  Şirketinizi <span className="gradient-text">Dinox</span> ile Yönetin
                </Title>
              </div>
            </div>

            {/* Modern Steps Navigation */}
            <div className="modern-steps-navigation" style={{ 
              marginBottom: '0', 
              marginTop: '0', 
              paddingBottom: '0' 
            }}>
              <div className="steps-container" style={{ marginBottom: '0' }}>
                {steps.map((step, index) => (
                  <div 
                    key={index} 
                    className={`modern-step ${
                      index === currentStep ? 'active' : 
                      completedSteps.includes(index) ? 'completed' : 'pending'
                    }`}
                    style={{ 
                      marginBottom: '0', 
                      paddingBottom: '10px' 
                    }}
                  >
                    <div className="step-connector" />
                    <div className="step-circle">
                      <div className="step-inner">
                        {completedSteps.includes(index) ? 
                          <CheckCircleFilled className="step-icon completed" /> : 
                          stepLoading === index || (index === 2 && productsLoading) ? 
                          <LoadingOutlined className="step-icon loading" style={{ fontSize: '20px' }} /> :
                          <span style={{ fontSize: '20px' }}>{step.icon}</span>
                        }
                      </div>
                    </div>
                    <div className="step-content">
                      <Text className="step-title" style={{ fontSize: '12px', fontWeight: 500 }}>{step.title}</Text>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Main Content */}
            <div className="modern-main-content" style={{ 
              marginTop: '-30px', 
              paddingTop: '0'
            }}>
              {getCurrentStepContent()}
            </div>

            {/* Modern Footer */}
            <div className="modern-footer" style={{ textAlign: 'center', marginTop: '40px' }}>
              <div className="payment-security" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
                <div className="security-badges" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <img src="assets/img/mastercardlogo.png" alt="Mastercard" style={{ height: '24px', margin: '0 8px' }} />
                  <img src="assets/img/visalogo.png" alt="Visa" style={{ height: '24px', margin: '0 8px' }} />
                  <img src="assets/img/iyzico.png" alt="Iyzico" style={{ height: '24px', margin: '0 8px' }} />
                </div>
              </div>
              
              <div className="footer-copyright" style={{ marginTop: '20px' }}>
                <Text style={{ fontSize: '14px', color: 'white' }}>
                  © {new Date().getFullYear()} Dinox. Tüm hakları saklıdır.
                </Text>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login3;
